import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { 
  Mail, Upload, Send, Users, FileSpreadsheet, Eye, 
  CheckCircle, XCircle, Clock, AlertTriangle, Loader2,
  ChevronDown, ChevronUp, Trash2, Save, FolderOpen, Plus
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminBulkEmail() {
  const [recipients, setRecipients] = useState([]);
  const [subject, setSubject] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [invalidEmails, setInvalidEmails] = useState([]);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const fileInputRef = useRef(null);
  
  // Template states
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("admin_token");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/admin/bulk-email/campaigns`, getAuthHeaders());
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/bulk-email/templates`, getAuthHeaders());
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error("Bitte eine Excel-Datei (.xlsx) hochladen");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `${API}/admin/bulk-email/parse-excel`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      setRecipients(response.data.recipients);
      setInvalidEmails(response.data.invalid_emails || []);
      
      if (response.data.invalid_emails?.length > 0) {
        toast.warning(`${response.data.total_valid} gültige E-Mails gefunden, ${response.data.invalid_emails.length} ungültige übersprungen`);
      } else {
        toast.success(`${response.data.total_valid} Empfänger erfolgreich importiert`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Importieren");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail || !subject || !htmlContent) {
      toast.error("Bitte Betreff, Inhalt und Test-E-Mail-Adresse angeben");
      return;
    }

    try {
      await axios.post(
        `${API}/admin/bulk-email/test`,
        {
          subject,
          html_content: htmlContent,
          test_email: testEmail
        },
        getAuthHeaders()
      );
      toast.success(`Test-E-Mail an ${testEmail} gesendet`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Senden der Test-E-Mail");
    }
  };

  const handleSendBulkEmail = async () => {
    if (recipients.length === 0) {
      toast.error("Keine Empfänger vorhanden");
      return;
    }
    if (!subject || !htmlContent) {
      toast.error("Bitte Betreff und Inhalt angeben");
      return;
    }

    if (!window.confirm(`Sind Sie sicher, dass Sie ${recipients.length} E-Mails versenden möchten?`)) {
      return;
    }

    setIsSending(true);
    try {
      const response = await axios.post(
        `${API}/admin/bulk-email/send`,
        {
          subject,
          html_content: htmlContent,
          recipients
        },
        getAuthHeaders()
      );

      if (response.data.failed_count > 0) {
        toast.warning(`${response.data.sent_count} gesendet, ${response.data.failed_count} fehlgeschlagen`);
      } else {
        toast.success(`${response.data.sent_count} E-Mails erfolgreich gesendet!`);
      }

      // Reset form
      setRecipients([]);
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Versenden");
    } finally {
      setIsSending(false);
    }
  };

  // Template functions
  const handleSaveTemplate = async () => {
    if (!templateName || !subject || !htmlContent) {
      toast.error("Bitte Name, Betreff und Inhalt angeben");
      return;
    }

    setIsSavingTemplate(true);
    try {
      await axios.post(
        `${API}/admin/bulk-email/templates`,
        {
          name: templateName,
          subject,
          html_content: htmlContent
        },
        getAuthHeaders()
      );
      toast.success("Vorlage gespeichert");
      setShowSaveDialog(false);
      setTemplateName("");
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleLoadTemplate = (template) => {
    setSubject(template.subject);
    setHtmlContent(template.html_content);
    setShowTemplates(false);
    toast.success(`Vorlage "${template.name}" geladen`);
  };

  const handleDeleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Vorlage "${templateName}" wirklich löschen?`)) return;

    try {
      await axios.delete(`${API}/admin/bulk-email/templates/${templateId}`, getAuthHeaders());
      toast.success("Vorlage gelöscht");
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <span className="flex items-center gap-1 text-green-600 text-xs"><CheckCircle className="w-3 h-3" /> Abgeschlossen</span>;
      case "completed_with_errors":
        return <span className="flex items-center gap-1 text-yellow-600 text-xs"><AlertTriangle className="w-3 h-3" /> Mit Fehlern</span>;
      case "sending":
        return <span className="flex items-center gap-1 text-blue-600 text-xs"><Loader2 className="w-3 h-3 animate-spin" /> Wird gesendet</span>;
      default:
        return <span className="flex items-center gap-1 text-slate-500 text-xs"><Clock className="w-3 h-3" /> Ausstehend</span>;
    }
  };

  // Email template with placeholders
  const defaultTemplate = `<div style="max-width: 600px; margin: 0 auto;">
  <h2 style="color: #6366f1;">Hallo {{name}},</h2>
  
  <p>Hier ist dein E-Mail-Text...</p>
  
  <p>Mit freundlichen Grüßen,<br>
  <strong>Lea von Factory-Hype</strong></p>
</div>`;

  return (
    <div className="space-y-6" data-testid="admin-bulk-email">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Mail className="w-6 h-6 text-blue-600" />
          Massen-E-Mail versenden
        </h2>
        <p className="text-slate-500 text-sm mt-1">
          Importiere eine Excel-Datei und versende personalisierte E-Mails an bis zu 200 Empfänger.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Upload & Recipients */}
        <div className="space-y-4">
          {/* Excel Upload */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-green-600" />
                1. Excel-Datei hochladen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="excel-upload"
                />
                <label
                  htmlFor="excel-upload"
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                >
                  {isUploading ? (
                    <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-slate-400 mb-2" />
                      <span className="text-sm text-slate-500">Excel-Datei hierher ziehen oder klicken</span>
                      <span className="text-xs text-slate-400 mt-1">Spalten: "Email" und "Name"</span>
                    </>
                  )}
                </label>

                {/* Placeholder Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                  <strong>Verfügbare Platzhalter:</strong>
                  <ul className="mt-1 space-y-0.5">
                    <li><code className="bg-blue-100 px-1 rounded">{"{{name}}"}</code> - Name des Empfängers</li>
                    <li><code className="bg-blue-100 px-1 rounded">{"{{spaltenname}}"}</code> - Beliebige Excel-Spalte</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recipients List */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-5 h-5 text-violet-600" />
                Empfänger ({recipients.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recipients.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">
                  Noch keine Empfänger importiert
                </p>
              ) : (
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {recipients.slice(0, 50).map((r, idx) => (
                    <div key={idx} className="flex items-center justify-between py-1 px-2 bg-slate-50 rounded text-xs">
                      <span className="font-medium">{r.email}</span>
                      {r.name && <span className="text-slate-500">{r.name}</span>}
                    </div>
                  ))}
                  {recipients.length > 50 && (
                    <p className="text-xs text-slate-400 text-center py-2">
                      ... und {recipients.length - 50} weitere
                    </p>
                  )}
                </div>
              )}

              {invalidEmails.length > 0 && (
                <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                  <strong>{invalidEmails.length} ungültige E-Mails übersprungen:</strong>
                  <ul className="mt-1">
                    {invalidEmails.slice(0, 5).map((e, idx) => (
                      <li key={idx}>Zeile {e.row}: {e.email}</li>
                    ))}
                    {invalidEmails.length > 5 && <li>...</li>}
                  </ul>
                </div>
              )}

              {recipients.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setRecipients([]); setInvalidEmails([]); }}
                  className="mt-3 w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Liste leeren
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Email Content */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  2. E-Mail verfassen
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowTemplates(!showTemplates)}
                    className="text-xs h-8"
                  >
                    <FolderOpen className="w-4 h-4 mr-1" />
                    Vorlagen ({templates.length})
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSaveDialog(true)}
                    disabled={!subject || !htmlContent}
                    className="text-xs h-8"
                  >
                    <Save className="w-4 h-4 mr-1" />
                    Speichern
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Templates Dropdown */}
              {showTemplates && (
                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-2">
                  <p className="text-xs font-medium text-slate-600">Gespeicherte Vorlagen:</p>
                  {templates.length === 0 ? (
                    <p className="text-xs text-slate-400">Keine Vorlagen gespeichert</p>
                  ) : (
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {templates.map((t) => (
                        <div key={t.id} className="flex items-center justify-between bg-white p-2 rounded border border-slate-200">
                          <button
                            onClick={() => handleLoadTemplate(t)}
                            className="text-sm text-left hover:text-blue-600 flex-1"
                          >
                            <span className="font-medium">{t.name}</span>
                            <span className="text-xs text-slate-400 ml-2">({t.subject.substring(0, 30)}...)</span>
                          </button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTemplate(t.id, t.name)}
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Save Template Dialog */}
              {showSaveDialog && (
                <div className="border border-blue-200 rounded-lg p-3 bg-blue-50 space-y-2">
                  <p className="text-xs font-medium text-blue-800">Vorlage speichern:</p>
                  <div className="flex gap-2">
                    <Input
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder="Vorlagen-Name eingeben..."
                      className="flex-1 text-sm"
                    />
                    <Button
                      onClick={handleSaveTemplate}
                      disabled={!templateName || isSavingTemplate}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {isSavingTemplate ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setShowSaveDialog(false); setTemplateName(""); }}
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Subject */}
              <div>
                <label className="text-sm font-medium text-slate-700">Betreff</label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="E-Mail Betreff..."
                  className="mt-1"
                />
              </div>

              {/* HTML Content */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-slate-700">Inhalt (HTML)</label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setHtmlContent(defaultTemplate)}
                    className="text-xs text-blue-600 h-6"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Standard-Vorlage
                  </Button>
                </div>
                <textarea
                  value={htmlContent}
                  onChange={(e) => setHtmlContent(e.target.value)}
                  placeholder="<p>Dein E-Mail-Inhalt...</p>"
                  rows={10}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Preview Button */}
              <Button
                variant="outline"
                onClick={() => setShowPreview(!showPreview)}
                className="w-full"
              >
                <Eye className="w-4 h-4 mr-2" />
                {showPreview ? "Vorschau ausblenden" : "Vorschau anzeigen"}
              </Button>

              {/* Preview */}
              {showPreview && htmlContent && (
                <div className="border border-slate-200 rounded-lg p-4 bg-white">
                  <p className="text-xs text-slate-500 mb-2">Vorschau:</p>
                  <div 
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ 
                      __html: htmlContent
                        .replace(/\{\{name\}\}/g, "Max Mustermann")
                        .replace(/\{\{Name\}\}/g, "Max Mustermann")
                    }} 
                  />
                </div>
              )}

              {/* Test Email */}
              <div className="flex gap-2">
                <Input
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Test-E-Mail-Adresse"
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  onClick={handleSendTestEmail}
                  disabled={!testEmail || !subject || !htmlContent}
                >
                  Test senden
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Send Button */}
          <Button
            onClick={handleSendBulkEmail}
            disabled={isSending || recipients.length === 0 || !subject || !htmlContent}
            className="w-full bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white py-6 text-base"
          >
            {isSending ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Wird gesendet...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                {recipients.length} E-Mails versenden
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Campaign History */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-600" />
            Kampagnen-Verlauf
          </CardTitle>
        </CardHeader>
        <CardContent>
          {campaigns.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">
              Noch keine Kampagnen gesendet
            </p>
          ) : (
            <div className="space-y-2">
              {campaigns.map((campaign) => (
                <div key={campaign.id} className="border border-slate-200 rounded-lg overflow-hidden">
                  <div 
                    className="flex items-center justify-between p-3 bg-slate-50 cursor-pointer hover:bg-slate-100"
                    onClick={() => setExpandedCampaign(expandedCampaign === campaign.id ? null : campaign.id)}
                  >
                    <div className="flex items-center gap-3">
                      {getStatusBadge(campaign.status)}
                      <span className="font-medium text-sm">{campaign.subject}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span>{campaign.sent_count}/{campaign.total_recipients} gesendet</span>
                      <span>{new Date(campaign.created_at).toLocaleDateString('de-DE')}</span>
                      {expandedCampaign === campaign.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </div>
                  
                  {expandedCampaign === campaign.id && (
                    <div className="p-3 border-t border-slate-200 text-xs space-y-2">
                      <p><strong>Erstellt:</strong> {new Date(campaign.created_at).toLocaleString('de-DE')}</p>
                      <p><strong>Gesendet:</strong> {campaign.sent_count} | <strong>Fehlgeschlagen:</strong> {campaign.failed_count}</p>
                      {campaign.errors?.length > 0 && (
                        <div className="mt-2 p-2 bg-red-50 rounded text-red-700">
                          <strong>Fehler:</strong>
                          <ul className="mt-1">
                            {campaign.errors.slice(0, 5).map((err, idx) => (
                              <li key={idx}>{err.email}: {err.error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
